import { Divider, Flex, Skeleton, Tag } from 'antd'
import dayjs from 'dayjs'
import { useContext } from 'react'
import { InvoiceSummaryWithCustomerJobData, wasInvoiceSent } from '../../../domain/invoicing/invoice'
import { useCustomerEmailsQuery } from '../../../domain/customers/customerQueries'
import { getBestCustomerEmail } from '../../../domain/customers/customer'
import { useInvoiceDetailsQuery } from '../../../domain/invoicing/invoicingQueries'
import { AuthenticationContext } from '../../../AuthenticationContext'

type Props = {
  invoiceSummary: InvoiceSummaryWithCustomerJobData
}

const formatDate = (iso: string | null | undefined) => {
  if (!iso) {
    return null
  }
  return dayjs(iso).format('MMM D, YYYY')
}

const InvoiceDetailStatusBar = ({ invoiceSummary }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const customerEmailsQuery = useCustomerEmailsQuery(invoiceSummary.customerId)
  const detailsQuery = useInvoiceDetailsQuery(invoiceSummary.invoiceId)

  if (customerEmailsQuery.isLoading || detailsQuery.isLoading) {
    return <Skeleton.Input active block size="small" />
  }

  const primaryEmail = getBestCustomerEmail(customerEmailsQuery.data ?? []) ?? '—'

  const wasSent = wasInvoiceSent(invoiceSummary)
  const sentAt = formatDate(invoiceSummary.emailSentAt)

  const isTenantConnectedToQuickBooks = currentUser?.isTenantConnectedToQuickBooks === true
  const showQuickBooksSection = isTenantConnectedToQuickBooks || invoiceSummary.invoicingSystem === 'quickBooks'
  const isSynced = invoiceSummary.qboInvoiceId.length > 0
  const syncedAt = formatDate(detailsQuery.data?.quickBooksInvoice?.lastUpdatedAt)
  const dueDate = formatDate(invoiceSummary.dueOn)

  return (
    <Flex align="center" gap={8} wrap="wrap" style={{ padding: '8px 0' }}>
      <strong>Primary Email:</strong>
      <span>{primaryEmail}</span>

      <Divider type="vertical" />

      <Tag color={wasSent ? 'success' : 'error'} style={{ margin: 0 }}>
        {wasSent ? 'Sent' : 'Not Sent'}
      </Tag>
      {wasSent && sentAt && <span>{sentAt}</span>}

      {showQuickBooksSection && <>
        <Divider type="vertical" />
        <strong>QuickBooks:</strong>
        <Tag color={isSynced ? 'success' : 'error'} style={{ margin: 0 }}>
          {isSynced ? 'Synced' : 'Not Synced'}
        </Tag>
        {isSynced && syncedAt && <span>{syncedAt}</span>}
      </>}

      <Divider type="vertical" />
      <strong>Due:</strong>
      <span>{dueDate ?? '—'}</span>
    </Flex>
  )
}

export default InvoiceDetailStatusBar
